<template>
  <div class="fullscreen-page">
    <HeaderMenu title="Settings" />
    <main class="settings-main">
      <div class="card">
        <h2>Display Formats</h2>

        <div class="form-group">
          <label for="date-format">Date Format:</label>
          <select v-model="formData.date_format" id="date-format">
            <option value="MM/DD/YYYY">MM/DD/YYYY (12/25/2024)</option>
            <option value="DD.MM.YYYY">DD.MM.YYYY (25.12.2024)</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-25)</option>
          </select>
        </div>

        <div class="form-group">
          <label for="time-format">Time Format:</label>
          <select v-model="formData.time_format" id="time-format">
            <option value="HH:mm">24-hour (14:30)</option>
            <option value="HH:mm:ss">24-hour with seconds (14:30:45)</option>
          </select>
        </div>

        <div class="form-group">
          <label for="fasting-interval">Default Fasting Interval (hours):</label>
          <input
            v-model.number="formData.fasting_interval"
            id="fasting-interval"
            type="number"
            min="1"
            max="72"
            step="0.5"
          />
        </div>

        <div class="form-actions">
          <button @click="handleSave" class="primary btn" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
          <button @click="handleCancel" class="secondary btn">
            Discard
          </button>
        </div>
      </div>

      <div class="card">
        <h3>Preview</h3>
        <p>
          <strong>Date:</strong> {{ previewDate }}
        </p>
        <p>
          <strong>Time:</strong> {{ previewTime }}
        </p>
        <p>
          <strong>Fasting Interval:</strong> {{ previewInterval }}
        </p>
      </div>

      <div class="card">
        <h2>Change Password</h2>
        <p class="section-description">Update your password. You will be logged out on all other devices.</p>

        <div class="form-group">
          <label for="current-password">Current Password:</label>
          <input
            v-model="passwordForm.currentPassword"
            id="current-password"
            type="password"
            autocomplete="current-password"
            @keydown.enter="handleChangePassword"
          />
        </div>

        <div class="form-group">
          <label for="new-password">New Password:</label>
          <input
            v-model="passwordForm.newPassword"
            id="new-password"
            type="password"
            autocomplete="new-password"
            @input="clearPasswordError"
            @keydown.enter="handleChangePassword"
          />
        </div>

        <div class="form-group">
          <label for="confirm-password">Confirm New Password:</label>
          <input
            v-model="passwordForm.confirmPassword"
            id="confirm-password"
            type="password"
            autocomplete="new-password"
            @input="clearPasswordError"
            @keydown.enter="handleChangePassword"
          />
        </div>

        <div v-if="passwordErrors.length > 0" class="validation-errors">
          <p v-for="(err, i) in passwordErrors" :key="i">{{ err }}</p>
        </div>

        <div class="form-actions">
          <button @click="handleChangePassword" class="primary btn" :disabled="changingPassword">
            {{ changingPassword ? 'Changing...' : 'Change Password' }}
          </button>
        </div>
      </div>

      <div class="card">
        <h2>Session Information</h2>
        <p class="section-description">Check the validity of your authentication tokens</p>
        
        <div class="token-status-section">
          <div class="token-check">
            <h4>Access Token</h4>
            <button 
              @click="checkAccessTokenStatus" 
              class="secondary btn"
              :disabled="loadingAccessToken"
            >
              {{ loadingAccessToken ? 'Checking...' : 'Check Status' }}
            </button>
            <div v-if="accessTokenStatus" class="status-result">
              <p v-if="!accessTokenStatus.isExpired" class="status-valid">
                <strong>Valid</strong> · Expires in {{ formatExpirationTime(accessTokenStatus.expiresInSeconds) }}
              </p>
              <p v-else class="status-expired">
                <strong>Expired</strong>
              </p>
              <small>{{ new Date(accessTokenStatus.expiresAt).toLocaleString() }}</small>
            </div>
            <div v-if="accessTokenError" class="status-error">
              {{ accessTokenError }}
            </div>
          </div>

          <div class="token-check">
            <h4>Refresh Token</h4>
            <button 
              @click="checkRefreshTokenStatus" 
              class="secondary btn"
              :disabled="loadingRefreshToken"
            >
              {{ loadingRefreshToken ? 'Checking...' : 'Check Status' }}
            </button>
            <div v-if="refreshTokenStatus" class="status-result">
              <p v-if="!refreshTokenStatus.isExpired" class="status-valid">
                <strong>Valid</strong> · Expires in {{ formatExpirationTime(refreshTokenStatus.expiresInSeconds) }}
              </p>
              <p v-else class="status-expired">
                <strong>Expired</strong>
              </p>
              <small>{{ new Date(refreshTokenStatus.expiresAt).toLocaleString() }}</small>
            </div>
            <div v-if="refreshTokenError" class="status-error">
              {{ refreshTokenError }}
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import HeaderMenu from '../components/HeaderMenu.vue'
import { useSettings } from '../composables/useSettings'
import { useNotifications } from '../composables/useNotifications'
import { API } from '../api/client'

interface TokenStatus {
  expiresAt: string
  expiresInSeconds: number
  isExpired: boolean
}

const { settings, loadSettings, saveSettings, formatDate, formatTime, formatInterval } = useSettings()
const { addNotification } = useNotifications()

const formData = ref({
  date_format: 'MM/DD/YYYY',
  time_format: 'HH:mm',
  fasting_interval: 16,
})

const saving = ref(false)
const changingPassword = ref(false)
const passwordErrors = ref<string[]>([])
const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

function clearPasswordError() {
  passwordErrors.value = []
}

function validateNewPassword(password: string): string[] {
  const errors: string[] = []
  if (password.length < 12) {
    errors.push('Password must be at least 12 characters')
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Password must include at least one lowercase letter')
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must include at least one uppercase letter')
  }
  if (!/\d/.test(password)) {
    errors.push('Password must include at least one digit')
  }
  return errors
}

const handleChangePassword = async () => {
  passwordErrors.value = []
  const { currentPassword, newPassword, confirmPassword } = passwordForm.value

  if (!currentPassword || !newPassword || !confirmPassword) {
    passwordErrors.value = ['All fields are required']
    return
  }

  if (newPassword !== confirmPassword) {
    passwordErrors.value = ['New passwords do not match']
    return
  }

  const validationErrors = validateNewPassword(newPassword)
  if (validationErrors.length > 0) {
    passwordErrors.value = validationErrors
    return
  }

  changingPassword.value = true
  try {
    const { token, refreshToken } = await API.changePassword(currentPassword, newPassword)
    API.setToken(token)
    localStorage.setItem('auth_token', token)
    localStorage.setItem('auth_refresh_token', refreshToken)
    addNotification('Password changed successfully!', { type: 'success' })
    passwordForm.value = { currentPassword: '', newPassword: '', confirmPassword: '' }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to change password'
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { error?: string } } }
      passwordErrors.value = [axiosError.response?.data?.error || message]
    } else {
      passwordErrors.value = [message]
    }
    addNotification(passwordErrors.value[0], { type: 'error' })
  } finally {
    changingPassword.value = false
  }
}

const loadingAccessToken = ref(false)
const loadingRefreshToken = ref(false)
const accessTokenStatus = ref<TokenStatus | null>(null)
const refreshTokenStatus = ref<TokenStatus | null>(null)
const accessTokenError = ref<string | null>(null)
const refreshTokenError = ref<string | null>(null)

const previewDate = computed(() => formatDate(new Date()))
const previewTime = computed(() => formatTime(new Date()))
const previewInterval = computed(() => formatInterval(formData.value.fasting_interval))

function formatExpirationTime(seconds: number): string {
  if (seconds < 0) {
    return 'Expired'
  }
  
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  
  if (days > 0) {
    return `${days}d ${hours}h`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else {
    return `${minutes}m`
  }
}

const checkAccessTokenStatus = async () => {
  loadingAccessToken.value = true
  accessTokenError.value = null
  accessTokenStatus.value = null
  
  try {
    const status = await API.checkAccessTokenStatus()
    accessTokenStatus.value = status
    if (!status.isExpired) {
      addNotification('Access token is valid', { type: 'success' })
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to check access token status'
    accessTokenError.value = message
    addNotification(message, { type: 'error' })
  } finally {
    loadingAccessToken.value = false
  }
}

const checkRefreshTokenStatus = async () => {
  loadingRefreshToken.value = true
  refreshTokenError.value = null
  refreshTokenStatus.value = null
  
  try {
    const refreshToken = localStorage.getItem('auth_refresh_token')
    if (!refreshToken) {
      throw new Error('No refresh token found')
    }
    
    const status = await API.checkRefreshTokenStatus(refreshToken)
    refreshTokenStatus.value = status
    if (!status.isExpired) {
      addNotification('Refresh token is valid', { type: 'success' })
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to check refresh token status'
    refreshTokenError.value = message
    addNotification(message, { type: 'error' })
  } finally {
    loadingRefreshToken.value = false
  }
}

const handleSave = async () => {
  saving.value = true

  const success = await saveSettings(formData.value)
  if (success) {
    addNotification('Settings saved successfully!', { type: 'success' })
  } else {
    addNotification('Failed to save settings', { type: 'error' })
  }

  saving.value = false
}

const handleCancel = () => {
  formData.value = { ...settings.value }
}

onMounted(async () => {
  await loadSettings()
  formData.value = { ...settings.value }
})
</script>

<style scoped>
.settings-main {
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.form-group input,
.form-group select {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 2rem;
}

.message {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.section-description {
  color: #666;
  font-size: 0.9rem;
  margin-top: -0.5rem;
}

.token-status-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.token-check {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}

.token-check h4 {
  margin: 0;
  font-size: 0.95rem;
  color: #333;
}

.token-check button {
  align-self: flex-start;
  min-width: 120px;
}

.status-result {
  padding: 0.75rem;
  background-color: #fff;
  border-radius: 4px;
  border-left: 4px solid #667eea;
}

.status-result p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
}

.status-result small {
  display: block;
  color: #999;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.status-valid {
  color: #28a745;
}

.status-expired {
  color: #dc3545;
}

.status-error {
  padding: 0.75rem;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.validation-errors {
  padding: 0.75rem;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 4px;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.validation-errors p {
  margin: 0.25rem 0;
}
</style>
